import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_PRICING = {
  fan_plan_id: "ysv_fan_monthly",
  member_plan_id: "ysv_member_monthly",
  fan_label: "$10/month",
  member_label: "$12/month",
  fan_display: "$10",
  member_display: "$12",
  monthly_plan_id: "ysv_member_monthly",
  monthly_label: "$12/month",
  monthly_display: "$12",
  product_name: "YSV Member",
  plans: [],
};

export const formatPlanDisplay = (plan) => {
  if (!plan) return null;
  const amount = Number(plan.amount || 0);
  return amount % 1 === 0 ? `$${amount.toFixed(0)}` : `$${amount.toFixed(2)}`;
};

export const resolvePricing = (plans) => {
  const list = Array.isArray(plans) ? plans : [];
  const fan = list.find(p => p.tier === "fan" && p.interval === "month") || null;
  const member = list.find(p => p.tier === "member" && p.interval === "month") || null;
  if (!fan && !member) return null;
  return {
    fan_plan_id: fan?.id || DEFAULT_PRICING.fan_plan_id,
    member_plan_id: member?.id || DEFAULT_PRICING.member_plan_id,
    fan_label: fan ? (fan.label || `${formatPlanDisplay(fan)}/month`) : DEFAULT_PRICING.fan_label,
    member_label: member ? (member.label || `${formatPlanDisplay(member)}/month`) : DEFAULT_PRICING.member_label,
    fan_display: formatPlanDisplay(fan) || DEFAULT_PRICING.fan_display,
    member_display: formatPlanDisplay(member) || DEFAULT_PRICING.member_display,
    monthly_plan_id: member?.id || DEFAULT_PRICING.monthly_plan_id,
    monthly_label: member ? (member.label || `${formatPlanDisplay(member)}/month`) : DEFAULT_PRICING.monthly_label,
    monthly_display: formatPlanDisplay(member) || DEFAULT_PRICING.monthly_display,
    product_name: "YSV Member",
    plans: list,
  };
};

/** Resolve Fan or Member at click time if the initial plans request failed. */
export const planIdForCheckout = async (client, pricing, tier = "member") => {
  const wantedTier = tier === "fan" ? "fan" : "member";
  const pick = p => wantedTier === "fan" ? p.fan_plan_id : p.member_plan_id;
  if (pricing?.resolved) return pick(pricing);
  const path = localStorage.getItem("ysv_token")
    ? "/subscription/plans" : "/public/subscription/plans";
  const response = await client.get(path);
  return pick(resolvePricing(response.data) || DEFAULT_PRICING);
};

// League types no longer select different billing products. Both historical
// labels now point to the Member price.
export const monthlyLabelForTier = (pricing, tier) => {
  if (tier === "fan") return pricing?.fan_label || null;
  if (["member", "youth", "amateur"].includes(tier)) return pricing?.member_label || null;
  return null;
};

export const annualDiscountAnswer = () =>
  "New subscriptions are monthly only. You can cancel anytime from your YSV profile.";

// Stand-in for any tier-dependent number or product name while the plans call
// is still in flight. Until it settles `pricing` holds the youth defaults, and
// an amateur must not see the youth product name or price flash before its real plan lands.
export const PENDING_DISPLAY = "—";
export const whenReady = (pricing, value, placeholder = PENDING_DISPLAY) =>
  (pricing?.ready ? value : placeholder);

/**
 * Label for the plan a user is already subscribed to (`user.subscription_plan_id`),
 * read off the plans the backend returned rather than guessed from a hardcoded
 * price — an account must not be labeled with another tier's amount.
 */
export const labelForPlanId = (pricing, planId) => {
  const hit = (pricing?.plans || []).find(p => p.id === planId);
  if (hit) return hit.label || formatPlanDisplay(hit);
  if (planId === "ysv_fan_monthly") return pricing?.fan_label || DEFAULT_PRICING.fan_label;
  return pricing?.member_label || DEFAULT_PRICING.member_label;
};

export const useDynamicPricing = () => {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [state, setState] = useState({ ready: false, resolved: false });
  const { api } = useAuth() || {};
  useEffect(() => {
    const token = localStorage.getItem("ysv_token");
    let cancelled = false;
    const client = api ? api() : axios.create({ baseURL: API });
    client.get(token ? "/subscription/plans" : "/public/subscription/plans")
      .then(response => {
        if (cancelled) return;
        const resolved = resolvePricing(response.data);
        if (resolved) setPricing(resolved);
        setState({ ready: true, resolved: !!resolved });
      })
      .catch(() => { if (!cancelled) setState({ ready: true, resolved: false }); });
    return () => { cancelled = true; };
  }, [api]);
  return { ...pricing, ...state };
};
